import { useState, useEffect, useCallback, useRef } from "react";
import {
  getEnergySources,
  getKpiSourceMappings,
  getFrameworks,
  getReportingQuestions,
} from "./energySourcesApi";
import { safeParseJSON, normaliseSource, energyKpiIds } from "./helpers";

/**
 * Owns everything the Energy Sources page fetches for one financial year.
 *
 * - `loading` is true only for blocking loads (first load / FY switch).
 * - `refresh()` re-fetches in the background after mutations.
 * - Responses are tagged with a fetch id; anything resolving after a newer
 *   fetch started is dropped, so a slow response can't clobber a newer FY.
 * - `loadToken` increments on each successful load so the page can seed its
 *   editable staging buffer from fresh server truth.
 *
 * @param {number|undefined} fyId Selected financial year id.
 * @returns {object} Server-truth data, load flags, and refresh/reload actions.
 */
export function useEnergySourcesData(fyId) {
  const [allQuestions, setAllQuestions] = useState([]);
  const [sources, setSources] = useState([]);
  const [serverKpiSourceMap, setServerKpiSourceMap] = useState({});
  const [selectableIds, setSelectableIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadToken, setLoadToken] = useState(0);

  const fetchIdRef = useRef(0);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!fyId) {
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    const isCurrent = () => fetchId === fetchIdRef.current;

    if (background) setRefreshing(true);
    else setLoading(true);

    try {
      const [sourcesRes, mappingsRes] = await Promise.all([
        getEnergySources(),
        getKpiSourceMappings(fyId),
      ]);

      const rawSources = sourcesRes?.data?.sourceList || sourcesRes?.data?.data?.sourceList || [];
      const serverMap = mappingsRes?.data?.data?.kpiSourceMap || {};

      const userId = safeParseJSON(localStorage.getItem("user_temp_id"));
      const fwRes = await getFrameworks(userId);
      const frameworks = fwRes?.data?.data || [];
      const qRes = await getReportingQuestions(fyId, frameworks.map(f => f.id));
      const allFetched = qRes?.data?.data || [];

      const ids = energyKpiIds(allFetched);
      const questions = allFetched.filter(q => ids.has(q.questionId));

      if (!isCurrent()) return;

      setSources(Array.isArray(rawSources) ? rawSources.map(normaliseSource) : []);
      setServerKpiSourceMap(serverMap);
      setSelectableIds(ids);
      setAllQuestions(questions);
      setError(null);
      setLoadToken(t => t + 1);
    } catch (e) {
      console.error(e);
      if (isCurrent()) setError("Failed to load energy sources. Please try again.");
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fyId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Background refresh for after-mutation updates — never blanks the page. */
  const refresh = useCallback(() => load({ background: true }), [load]);

  /** Blocking reload for the error-state Retry button. */
  const reload = useCallback(() => load(), [load]);

  return {
    allQuestions, sources, serverKpiSourceMap, selectableIds,
    loading, refreshing, error, loadToken, refresh, reload,
  };
}
